import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Shield, User, CreditCard, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function CreateAgencyModal({ isOpen, onClose, request, onCreated }) {
    const [loading, setLoading] = useState(false);
    const [createdAgency, setCreatedAgency] = useState(null);
    const [formData, setFormData] = useState({
        company_name: '', siren: '', siret: '', address: '', city: '', postal_code: '',
        phone: '', carte_t_number: '', carte_t_cci: '', rcp_assurer: '', rcp_numero_police: '',
        legal_rep_first_name: '', legal_rep_last_name: '', legal_rep_email: '', legal_rep_phone: '',
        billing_cycle: 'monthly'
    });

    useEffect(() => {
        if (request) {
            const nameParts = (request.contact_name || '').split(' ');
            setFormData(prev => ({
                ...prev,
                company_name: request.company_name || '',
                legal_rep_email: request.email || '',
                legal_rep_phone: request.phone || '',
                legal_rep_first_name: nameParts[0] || '',
                legal_rep_last_name: nameParts.slice(1).join(' ') || ''
            }));
            setCreatedAgency(null);
        }
    }, [request]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await api.post('/agency-onboarding/create-agency', { ...formData, demo_request_id: request?.id });
            setCreatedAgency(response.data);
            toast.success('Agence créée avec succès !');
            onCreated();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally { setLoading(false); }
    };

    if (!isOpen) return null;

    const Field = ({ label, value, onChange, required, type = 'text', span = 1, placeholder, maxLength }) => (
        <div className={span === 2 ? 'col-span-2' : ''}>
            <label className="block text-sm text-white/60 mb-1">{label}{required && ' *'}</label>
            <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required}
                placeholder={placeholder} maxLength={maxLength}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                className="bg-slate-900 rounded-2xl w-full max-w-2xl border border-white/10 shadow-2xl my-8" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-white/10">
                    <h2 className="text-xl font-bold text-white flex items-center gap-3">
                        <Building2 className="w-6 h-6 text-purple-400" />
                        {createdAgency ? 'Agence Créée !' : 'Créer une Agence'}
                    </h2>
                    {request && !createdAgency && <p className="text-white/60 text-sm mt-1">Depuis la demande de {request.company_name}</p>}
                </div>

                {createdAgency ? (
                    <div className="p-6 space-y-6">
                        <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-xl">
                            <div className="flex items-center gap-2 text-green-400 font-medium mb-2"><Check className="w-5 h-5" />Agence créée avec succès</div>
                            <p className="text-white/70 text-sm">Activez-la depuis l'onglet Agences pour envoyer l'email d'invitation.</p>
                        </div>
                        <div className="space-y-4">
                            <div className="p-4 bg-white/5 rounded-xl"><p className="text-white/60 text-sm mb-1">Agence</p><p className="text-white font-medium">{createdAgency.agency?.name}</p></div>
                            <div className="p-4 bg-white/5 rounded-xl"><p className="text-white/60 text-sm mb-1">Email du gérant</p><p className="text-white font-medium">{createdAgency.admin?.email}</p></div>
                        </div>
                        <div className="p-4 bg-purple-500/20 border border-purple-500/30 rounded-xl">
                            <p className="text-white/70 text-sm font-medium mb-2">Prochaines étapes :</p>
                            <ul className="space-y-1 text-white/60 text-sm">
                                {['Vérifiez les informations dans l\'onglet Agences', 'Cliquez "Activer" pour envoyer l\'email au gérant', 'Le gérant crée son mot de passe et accède à Homly'].map((step, i) => (
                                    <li key={i} className="flex items-center gap-2">
                                        <span className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-400 text-xs flex items-center justify-center">{i + 1}</span>{step}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="flex justify-end"><Button onClick={onClose} className="bg-purple-500 hover:bg-purple-600">Fermer</Button></div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                        <div>
                            <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-purple-400" />Informations Entreprise</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Nom de l'agence" value={formData.company_name} onChange={v => setFormData({...formData, company_name: v})} required span={2} />
                                <Field label="SIREN" value={formData.siren} onChange={v => setFormData({...formData, siren: v})} required maxLength={9} placeholder="123456789" />
                                <Field label="SIRET" value={formData.siret} onChange={v => setFormData({...formData, siret: v})} maxLength={14} placeholder="12345678901234" />
                                <Field label="Adresse" value={formData.address} onChange={v => setFormData({...formData, address: v})} required span={2} />
                                <Field label="Ville" value={formData.city} onChange={v => setFormData({...formData, city: v})} required />
                                <Field label="Code postal" value={formData.postal_code} onChange={v => setFormData({...formData, postal_code: v})} required maxLength={5} />
                                <Field label="Téléphone agence" value={formData.phone} onChange={v => setFormData({...formData, phone: v})} required type="tel" span={2} />
                            </div>
                        </div>
                        <div>
                            <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-purple-400" />Carte Professionnelle & Assurance</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="N° Carte T" value={formData.carte_t_number} onChange={v => setFormData({...formData, carte_t_number: v})} required placeholder="CPI XXXX XXXX XXXX" />
                                <Field label="CCI de délivrance" value={formData.carte_t_cci} onChange={v => setFormData({...formData, carte_t_cci: v})} placeholder="Ex: CCI Lyon" />
                                <Field label="Assureur RCP" value={formData.rcp_assurer} onChange={v => setFormData({...formData, rcp_assurer: v})} required placeholder="Ex: Allianz, AXA..." />
                                <Field label="N° Police RCP" value={formData.rcp_numero_police} onChange={v => setFormData({...formData, rcp_numero_police: v})} placeholder="Numéro de police" />
                            </div>
                        </div>
                        <div>
                            <h3 className="text-white font-medium mb-3 flex items-center gap-2"><User className="w-4 h-4 text-purple-400" />Responsable Légal (Admin)</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Prénom" value={formData.legal_rep_first_name} onChange={v => setFormData({...formData, legal_rep_first_name: v})} required />
                                <Field label="Nom" value={formData.legal_rep_last_name} onChange={v => setFormData({...formData, legal_rep_last_name: v})} required />
                                <Field label="Email" value={formData.legal_rep_email} onChange={v => setFormData({...formData, legal_rep_email: v})} required type="email" />
                                <Field label="Téléphone" value={formData.legal_rep_phone} onChange={v => setFormData({...formData, legal_rep_phone: v})} required type="tel" />
                            </div>
                        </div>
                        <div>
                            <h3 className="text-white font-medium mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4 text-purple-400" />Facturation</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {[{ key: 'monthly', title: 'Mensuel', desc: 'Facturation chaque mois' }, { key: 'annual', title: 'Annuel', desc: 'Économisez 10%' }].map(opt => (
                                    <button key={opt.key} type="button" onClick={() => setFormData({...formData, billing_cycle: opt.key})}
                                        className={`p-4 rounded-xl border text-left transition-all relative ${formData.billing_cycle === opt.key ? 'bg-purple-500/20 border-purple-500' : 'bg-white/5 border-white/10 hover:border-white/20'}`}>
                                        {opt.key === 'annual' && <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">-10%</span>}
                                        <p className="text-white font-medium">{opt.title}</p>
                                        <p className="text-white/60 text-sm">{opt.desc}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-3 justify-end pt-4 border-t border-white/10">
                            <Button type="button" onClick={onClose} variant="outline" className="border-white/20 text-white">Annuler</Button>
                            <Button type="submit" disabled={loading} className="bg-purple-500 hover:bg-purple-600">{loading ? 'Création...' : 'Créer l\'agence'}</Button>
                        </div>
                    </form>
                )}
            </motion.div>
        </div>
    );
}
